import { useEffect, useState } from 'react';
import { Draft, produce } from 'immer';

type SetsConfig = Models.SetsPlayground.SetsConfig;
type SetDefinition = Models.SetsPlayground.SetDefinition;
type SetDistribution = Models.SetsPlayground.SetDistribution;
type SetRanking = Models.SetsPlayground.SetRanking;
type SetFilterRequest = Models.SetsPlayground.SetFilterRequest;

interface UseSetsConfigReturn {
    galleryUrl: string;
    productKey: string;
    updateGalleryUrl: (url: string) => void;
    updateSetProductKey: (key: string) => void;
    config: SetsConfig | null;
    setConfig: (config: SetsConfig) => void;
    addSet: () => void;
    updateSet: (index: number, update: Partial<SetDefinition>) => void;
    deleteSet: (index: number) => void;
    updateSetName: (index: number, name: string) => void;
    updateSetPriority: (index: number, priority: number) => void;
    updateSetDistribution: (index: number, distribution: SetDistribution) => void;
    updateSetRanking: (index: number, ranking: SetRanking) => void;
    updateSetFilter: (index: number, filter: SetFilterRequest) => void;
    updateSetDesignPersonalizationContextId: (index: number, id: string) => void;
    updateSetContentFilter: (index: number, content: NonNullable<SetFilterRequest['content']>) => void;
    updateSetProductFilter: (index: number, productOptions: Record<string, string[]>) => void;
    updateSetCategoryFilter: (index: number, category: string[][]) => void;
}

export function useSetsConfig(): UseSetsConfigReturn {
    const [galleryUrl, setGalleryUrl] = useState<string>('');
    const [productKey, setProductKey] = useState<string>('');
    // TODO: Added this temp initial config to demo the sets JSOn view.
    // Remove this for the true initial config when ready.
    const [config, setConfigState] = useState<SetsConfig | null>({
        sets: [
            {
                name: 'Set 1',
                description: 'High-priority products with special promotions',
                priority: 1,
                distribution: {
                    type: 'probability',
                    part: 0.5,
                    offset: 0,
                    limit: 10,
                    seed: '12345',
                },
                ranking: {
                    type: 'forced',
                    rankingStrategies: [{ name: '', sortingDirection: 'descending' }],
                },
                filters: {
                    product: { productOptions: {} },
                    category: [],
                    content: { },
                },
            },
        ],
    });

    const setConfig = (newConfig: SetsConfig): void => {
        setConfigState(newConfig);
    };

    const updateGalleryUrl = (url: string): void => {
        setGalleryUrl(url);
    };

    const updateSetProductKey = (key: string): void => {
        setProductKey(key);
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        const gallery = params.get('galleryUrl');

        const key = params.get('productKey');

        const rawSets = params.get('rawSets');

        if (!rawSets || !gallery) {
            return;
        }

        setGalleryUrl(decodeURIComponent(gallery));
        setProductKey(key ?? '');
        try {
            const decoded = decodeURIComponent(rawSets);
            const parsed = JSON.parse(decoded);

            if (Array.isArray(parsed)) {
                setConfigState({ sets: parsed });
            } else {
                setConfigState({ sets: [] });
            }
        } catch {
            setConfigState({ sets: [] });
        }
    }, []);

    const addSet = (): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<SetsConfig> | null) => {
            if (!draft) {
                return;
            }

            const priorities = draft.sets.map((s) => s.priority ?? 0);
            const maxPriority = priorities.length ? Math.max(...priorities) : 0;

            const nextPriority = maxPriority + 1;

            draft.sets.push({
                name: `Set ${nextPriority}`,
                description: '',
                priority: nextPriority,
                distribution: {
                    type: 'probability',
                    part: 0,
                    offset: 0,
                    limit: 0,
                },
                ranking: {
                    type: 'forced',
                    rankingStrategies: [{ name: '', sortingDirection: 'descending' }],
                },
            });
        }));
    };

    const updateSet = (index: number, update: Partial<SetDefinition>): void => {
        setConfigState((prev) => {
            if (!prev) {
                return null;
            }
            return produce(prev, (draft: Draft<SetsConfig>) => {
                if (index >= 0 && index < draft.sets.length) {
                    // eslint-disable-next-line no-param-reassign
                    draft.sets[index] = { ...draft.sets[index], ...update };
                }
            });
        });
    };

    const deleteSet = (index: number): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<SetsConfig> | null) => {
            if (!draft) {
                return;
            }
            if (index < 0 || index >= draft.sets.length) {
                return;
            }

            draft.sets.splice(index, 1);
        }));
    };

    const updateSetName = (index: number, name: string): void => {
        updateSet(index, { name });
    };

    const updateSetPriority = (index: number, priority: number): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<SetsConfig> | null) => {
            if (!draft) {
                return;
            }

            const { sets } = draft;
            const item = sets[index];

            if (!item) {
                return;
            }

            const newPriority = Math.floor(priority);

            item.priority = newPriority;

            const taken = new Map<number, number>();

            for (let i = 0; i < sets.length; i++) {
                if (i !== index) {
                    const p = sets[i].priority;

                    taken.set(p, i);
                }
            }

            const current = newPriority;

            while (taken.has(current)) {
                const ownerIdx = taken.get(current);

                if (ownerIdx === undefined) {
                    break;
                }

                let next = current + 1;

                while (taken.has(next)) { next += 1; }

                taken.delete(current);
                taken.set(next, ownerIdx);
                sets[ownerIdx].priority = next;
            }

            draft.sets.sort((a, b) => a.priority - b.priority);
        }));
    };

    const updateSetDistribution = (index: number, distribution: SetDistribution): void => {
        updateSet(index, { distribution });
    };

    const updateSetRanking = (index: number, ranking: SetRanking): void => {
        updateSet(index, { ranking });
    };

    const updateSetFilter = (index: number, filter: SetFilterRequest): void => {
        updateSet(index, { filters: filter });
    };

    const updateSetContentFilter = (
        index: number,
        content: NonNullable<SetFilterRequest['content']>,
    ): void => {
        setConfigState((prev) => {
            if (!prev) {
                return prev;
            }
            return produce(prev, (draft: Draft<SetsConfig>) => {
                const target = draft.sets[index];

                if (!target) {
                    return;
                }
                const existing = target.filters ?? {
                    product: { productOptions: {} },
                    category: [],
                    content: {},
                };

                target.filters = {
                    ...existing,
                    content,
                };
            });
        });
    };

    const updateSetProductFilter = (
        index: number,
        productOptions: Record<string, string[]>,
    ): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<SetsConfig> | null) => {
            if (!draft) {
                return;
            }
            const s = draft.sets[index];

            if (!s) {
                return;
            }

            const prevFilter = s.filters ?? { product: { productOptions: {} }, category: [], content: {} };

            s.filters = {
                ...prevFilter,
                product: { productOptions },
            };
        }));
    };

    const updateSetCategoryFilter = (index: number, category: string[][]): void => {
        setConfigState((prev) => produce(prev, (draft: Draft<SetsConfig> | null) => {
            if (!draft) {
                return;
            }
            const s = draft.sets[index];

            if (!s) {
                return;
            }

            const prevFilter = s.filters ?? { product: { productOptions: {} }, category: [], content: {} };

            s.filters = {
                ...prevFilter,
                category,
            };
        }));
    };

    const updateSetDesignPersonalizationContextId = (index: number, id: string): void => {
        updateSet(index, { designPersonalizationContextId: id });
    };

    return {
        galleryUrl,
        productKey,
        updateSetProductKey,
        updateGalleryUrl,
        config,
        setConfig,
        addSet,
        updateSet,
        deleteSet,
        updateSetName,
        updateSetPriority,
        updateSetDistribution,
        updateSetRanking,
        updateSetFilter,
        updateSetDesignPersonalizationContextId,
        updateSetContentFilter,
        updateSetProductFilter,
        updateSetCategoryFilter,
    };
}
